import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { useGoogleAPI } from "@jti/fe-clients";
import { useTranslations } from "../../../../providers/useTranslations";
import { replaceStringPlaceholder } from "../../../../utils/replaceStringPlaceholder/replaceStringPlaceholder";
import { usePrizeLocatorContext } from "../../hooks";
import { GOOGLE_DIRECTION_URL_CONST } from "../../PrizeLocator";
import { getUpdatedLocation } from "../../utils/getUpdatedLocation";
import { VenuePopUp } from "../VenuePopUp";
import { MAP_STYLES_CONFIG } from "./prize-locator.config";
import ICONS from "./prize-locator-icons";
import styles from "../../PrizeLocator.module.scss";
const MOBILE_WIDTH = 959;
export const PrizeLocatorMap = ({ defaultLat, defaultLng, activeStoreId, stores, userMarkerPosition, loadMarkersFromApi, onMapLoaded, onSelectStore, flotiqApi, setStores, }) => {
    const { __ } = useTranslations();
    const _google = useGoogleAPI();
    const googleMapRef = useRef(null);
    const infoWindowRef = useRef(null);
    const [visibleMarkers, setVisibleMarkers] = useState([]);
    const [markerCluster, setMarkerCluster] = useState(null);
    const mapElementRef = useRef(null);
    const activeStoreRef = useRef(null);
    const { labels: { getDirectionLabel, venuePopUpOffersCount }, } = usePrizeLocatorContext();
    useEffect(() => {
        if (_google) {
            initMap();
        }
    }, [_google]);
    useEffect(() => {
        if (userMarkerPosition) {
            loadUserLocationMarker(userMarkerPosition);
        }
    }, [userMarkerPosition]);
    useEffect(() => {
        if (_google) {
            onMarkersLoaded(stores || []);
        }
        if (activeStoreRef.current) {
            onSelectStore(activeStoreRef.current);
            googleMapRef.current.setZoom(18);
            activeStoreRef.current = null;
        }
    }, [stores, _google, onSelectStore]);
    useEffect(() => {
        if (activeStoreId && googleMapRef.current) {
            const marker = visibleMarkers.find((marker) => marker.getTitle() === activeStoreId);
            if (marker) {
                google.maps.event.trigger(marker, "click");
            }
            else {
                getUpdatedLocation(activeStoreId, flotiqApi).then((response) => {
                    if (response.markers?.length > 0) {
                        setStores([...stores, ...response.markers]);
                        activeStoreRef.current = response.storeId;
                    }
                    else {
                        console.error("Configuration error: Location does not match Flotiq data.");
                    }
                });
            }
        }
    }, [activeStoreId, visibleMarkers]);
    const onMarkersLoaded = (stores) => {
        deleteMarkers();
        const defaultMarkerIcon = {
            url: "data:image/svg+xml;charset=utf-8," + encodeURIComponent(ICONS.DEFAULT_MARKER),
        };
        const activeMarkerIcon = {
            url: "data:image/svg+xml;charset=utf-8," + encodeURIComponent(ICONS.ACTIVE_MARKER),
        };
        const markers = stores.map(({ Location, ExternalId, StoreName, address }) => {
            const isMarkerSelected = activeStoreId === ExternalId;
            if (isMarkerSelected)
                activeStoreRef.current = ExternalId;
            const marker = new google.maps.Marker({
                position: {
                    lng: Location.lon,
                    lat: Location.lat,
                },
                title: ExternalId,
                icon: isMarkerSelected ? activeMarkerIcon : defaultMarkerIcon,
                optimized: true,
            });
            marker.addListener("click", () => {
                const isMarkerActive = marker["isActive"] && activeStoreId === marker.getTitle();
                if (isMarkerActive) {
                    deleteMarkers(isMarkerActive);
                    return;
                }
                deleteMarkers();
                if (infoWindowRef.current && googleMapRef.current && window.innerWidth > MOBILE_WIDTH) {
                    const container = document.createElement("div");
                    const targetNode = document.getElementById("storeLocatorCardList");
                    const root = createRoot(container);
                    const config = { attributeFilter: ["data-prizescount"] };
                    const callback = (mutationList, observer) => {
                        for (const mutation of mutationList) {
                            const prizesCountText = replaceStringPlaceholder(venuePopUpOffersCount, [{ placeholder: "count", value: mutation.target.dataset.prizescount }], false);
                            root.render(_jsx(VenuePopUp, { rewardsLabel: prizesCountText, address: address?.replaceAll("-", "&#x2011;"), getDirectionLabel: getDirectionLabel, venueName: StoreName, getDirectionUrl: `${GOOGLE_DIRECTION_URL_CONST}${Location.lat},${Location.lon}`, rel: "noreferrer", target: "_blank" }, StoreName));
                            infoWindowRef.current.setContent(container);
                            infoWindowRef.current.open(googleMapRef.current, marker);
                            observer.disconnect();
                        }
                    };
                    const observer = new MutationObserver(callback);
                    observer.observe(targetNode, config);
                    google.maps.event.addListener(infoWindowRef.current, "domready", function () {
                        const closeBtn = document.querySelector('button[title="Close"]');
                        const closeBtnSpan = document.querySelector('button[title="Close"] span');
                        if (closeBtn && closeBtnSpan) {
                            closeBtn.style.right = "0";
                            closeBtn.style.top = "0";
                            closeBtn.style.width = "auto";
                            closeBtn.style.height = "auto";
                            closeBtnSpan.style.width = "20px";
                            closeBtnSpan.style.height = "20px";
                        }
                    });
                    infoWindowRef.current.addListener("closeclick", () => {
                        markers.forEach((marker) => {
                            marker.setIcon(defaultMarkerIcon);
                        });
                    });
                }
                marker.setIcon(activeMarkerIcon);
                marker.set("isActive", true);
                onSelectStore(marker.getTitle() || "");
                if (googleMapRef.current) {
                    googleMapRef.current.setCenter({
                        lat: marker.getPosition()?.lat() || 0,
                        lng: marker.getPosition()?.lng() || 0,
                    });
                }
            });
            return marker;
        });
        const currentMarkerCluster = new MarkerClusterer({ map: googleMapRef.current, markers });
        setVisibleMarkers(markers);
        setMarkerCluster(currentMarkerCluster);
    };
    const deleteMarkers = (isSelected) => {
        if (markerCluster) {
            markerCluster.removeMarkers(visibleMarkers);
            markerCluster.clearMarkers();
        }
        if (isSelected) {
            onSelectStore("");
        }
    };
    const loadUserLocationMarker = (position) => {
        if (!googleMapRef.current) {
            return;
        }
        const image = {
            url: "data:image/svg+xml;charset=utf-8," + encodeURIComponent(ICONS.USER_LOCATION),
            scaledSize: new google.maps.Size(30, 30),
        };
        const userLocationInfoWindow = new google.maps.InfoWindow({
            content: `
        <div class="user-location-popup">
          <span>${__("storelocator.user_location.infowindow.label")}</span>
        </div>`,
        });
        const userLocationMarker = new google.maps.Marker({
            position: position,
            icon: image,
            map: googleMapRef.current,
        });
        userLocationMarker.addListener("click", () => {
            if (googleMapRef.current) {
                userLocationInfoWindow.open(googleMapRef.current, userLocationMarker);
            }
        });
    };
    const initMap = () => {
        const pos = {
            lat: defaultLat,
            lng: defaultLng,
        };
        if (!mapElementRef.current) {
            return;
        }
        const map = new google.maps.Map(mapElementRef.current, {
            center: { lat: pos.lat, lng: pos.lng },
            zoom: 15,
            mapTypeControl: false,
            scaleControl: false,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: false,
            styles: MAP_STYLES_CONFIG,
        });
        const infoWindow = new google.maps.InfoWindow({
            content: "",
        });
        infoWindowRef.current = infoWindow;
        googleMapRef.current = map;
        google.maps.event.addListenerOnce(map, "bounds_changed", () => {
            loadMarkersFromApi(map);
        });
        map.addListener("zoom_changed", () => {
            if (activeStoreRef.current)
                return;
            loadMarkersFromApi(map);
        });
        map.addListener("dragend", () => {
            loadMarkersFromApi(map);
        });
        map.addListener("resize", () => {
            loadMarkersFromApi(map);
        });
        infoWindow.addListener("closeclick", () => {
            onSelectStore("");
        });
        onMapLoaded(map);
    };
    return (_jsx("div", { className: styles.mapWrapper, children: _jsx("div", { ref: mapElementRef, className: styles.map, "data-testid": "storeLocatorMap" }) }));
};
//# sourceMappingURL=PrizeLocatorMap.js.map